import { useEffect, useRef, useState } from "react";
import { Images, Plus, Trash, UploadSimple, X } from "@phosphor-icons/react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminContent() {
  const { admin } = useAdminAuth();
  const canDelete = admin?.allow_delete !== false;
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({ section_title: "", files: [] });
  const [previews, setPreviews] = useState([]);
  const [error, setError] = useState("");
  const fileRef = useRef();

  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border text-sm outline-none transition-colors";
  const inputStyle = { borderColor: "rgba(26,92,55,0.2)", backgroundColor: "#F7F5F0", color: "#1C211F" };
  const labelCls = "block text-xs font-medium mb-1.5 uppercase tracking-wider";

  const fetchSections = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/content-sections`);
      const data = await res.json();
      setSections(Array.isArray(data) ? data : []);
    } catch {
      setSections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSections(); }, []);

  const resetForm = () => {
    previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    setForm({ section_title: "", files: [] });
    setPreviews([]);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setForm((current) => ({ ...current, files }));
    setPreviews(files.map((file) => ({ name: file.name, url: URL.createObjectURL(file) })));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.section_title.trim()) { setError("Section title is required."); return; }
    if (form.files.length === 0) { setError("Please upload at least one image."); return; }
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("section_title", form.section_title.trim());
      form.files.forEach((file) => fd.append("files", file));
      const res = await fetch(`${BACKEND_URL}/api/content-sections`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("Upload failed");
      await fetchSections();
      resetForm();
      setDrawerOpen(false);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await fetch(`${BACKEND_URL}/api/content-sections/${id}`, { method: "DELETE" });
      setSections((current) => current.filter((section) => section.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="font-heading text-xl font-medium" style={{ color: "#1C211F" }}>Content</h3>
          <p className="text-sm mt-0.5" style={{ color: "#4A5450" }}>
            {sections.length} section{sections.length !== 1 ? "s" : ""} configured for About Us
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setDrawerOpen(true); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
          data-testid="add-content-btn"
        >
          <Plus size={16} weight="bold" />
          Add content
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }} />
        </div>
      ) : sections.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
            <Images size={28} weight="duotone" style={{ color: "#1A5C37" }} />
          </div>
          <p className="text-sm font-medium" style={{ color: "#1C211F" }}>No content sections yet</p>
          <p className="text-xs mt-1" style={{ color: "#4A5450" }}>Click &quot;Add content&quot; to create the first About Us section.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sections.map((section) => (
            <div key={section.id} className="p-5 rounded-xl border" style={{ backgroundColor: "#fff", borderColor: "rgba(26,92,55,0.1)" }}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <p className="text-sm font-semibold" style={{ color: "#1C211F" }}>{section.section_title}</p>
                  <p className="text-xs mt-1" style={{ color: "#4A5450" }}>
                    {section.images?.length || 0} image{section.images?.length !== 1 ? "s" : ""}
                  </p>
                </div>
                {canDelete && (
                  <button
                    onClick={() => handleDelete(section.id)}
                    disabled={deletingId === section.id}
                    className="p-1.5 rounded-lg transition-colors"
                    style={{ color: "#B85C38" }}
                  >
                    <Trash size={15} weight="duotone" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {(section.images || []).slice(0, 6).map((image, idx) => (
                  <img
                    key={`${image.url}-${idx}`}
                    src={`${BACKEND_URL}${image.url}`}
                    alt={image.name || section.section_title}
                    className="h-20 w-full rounded-lg object-cover"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {drawerOpen && (
        <div className="fixed inset-0 z-40" style={{ backgroundColor: "rgba(0,0,0,0.35)" }} onClick={() => setDrawerOpen(false)} />
      )}

      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{ width: "min(480px, 100vw)", backgroundColor: "#fff", boxShadow: "-8px 0 32px rgba(0,0,0,0.12)" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <h3 className="font-heading text-lg font-medium" style={{ color: "#1C211F" }}>Add content</h3>
          <button onClick={() => setDrawerOpen(false)} className="p-1.5 rounded-lg" style={{ color: "#4A5450" }}>
            <X size={20} weight="bold" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Section Title</label>
            <input
              required
              value={form.section_title}
              onChange={(e) => setForm((current) => ({ ...current, section_title: e.target.value }))}
              className={inputCls}
              style={inputStyle}
              placeholder="e.g. Community initiatives"
            />
          </div>

          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Section Images</label>
            <div
              className="relative flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed cursor-pointer transition-colors"
              style={{ borderColor: previews.length ? "#1A5C37" : "rgba(26,92,55,0.25)", backgroundColor: "#F7F5F0" }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFilesChange} />
              <UploadSimple size={28} weight="duotone" style={{ color: "#1A5C37" }} />
              <p className="text-sm" style={{ color: "#4A5450" }}>Click to upload one or more images</p>
            </div>
          </div>

          {previews.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {previews.map((preview) => (
                <img key={preview.url} src={preview.url} alt={preview.name} className="h-24 w-full rounded-lg object-cover" />
              ))}
            </div>
          )}

          {error && (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#B85C38", backgroundColor: "rgba(184,92,56,0.08)" }}>
              {error}
            </p>
          )}
        </form>

        <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <button type="button" onClick={() => setDrawerOpen(false)} className="flex-1 py-2.5 rounded-lg text-sm font-medium border" style={{ borderColor: "rgba(26,92,55,0.2)", color: "#4A5450" }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={submitting} className="flex-1 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: submitting ? "#4a8c62" : "#1A5C37", color: "#F7F5F0" }}>
            {submitting ? "Saving..." : "Save content"}
          </button>
        </div>
      </div>
    </div>
  );
}
